import type { IContextualMenuItem, IContextualMenuProps } from '@fluentui/react';
import React from 'react';
import { useState } from 'owa-react';
import {
    Menu,
    MenuDivider,
    MenuItem,
    MenuList,
    type MenuOpenChangeData,
    MenuPopover,
    MenuTrigger,
    type MenuProps,
    type PositioningProps,
    type PositioningShorthandValue,
} from '@fluentui/react-components';
import { addDocumentEventListener } from 'owa-event-listener';

export interface MoveMenuProps {
    menuProps?: IContextualMenuProps;
    children?: React.ReactElement;
    open?: boolean;
    target?: HTMLElement;
    onMenuClose?: (shouldFocusTrigger: boolean) => unknown;
}

export const MoveMenu: React.FunctionComponent<MoveMenuProps> = React.memo(function ({
    open,
    menuProps,
    onMenuClose: onMenuCloseCallback,
    children,
    target,
}: MoveMenuProps) {
    const [isMoveMenuOpen, setMoveMenuOpen] = useState(false);
    const items = React.useMemo(
        () => (menuProps?.items || []).filter(item => !item.hidden),
        [menuProps]
    );
    const hasCustomRenderItems =
        items.length > 0 && items.every(item => typeof item.onRender === 'function');

    const [shouldLockMoveMenuPosition, setShouldLockMoveMenuPosition] = useState(false);
    const moveMenuFallbackPositions = React.useMemo<PositioningShorthandValue[]>(
        () => ['before', 'below-start', 'above-start', 'below-end', 'above-end'],
        []
    );

    const handleMoveMenuPositioningEnd = React.useCallback(() => {
        setShouldLockMoveMenuPosition(true);
    }, []);

    const moveMenuBasePositioning = React.useMemo<PositioningProps>(
        () => ({
            position: 'after',
            align: 'top',
            autoSize: 'height',
            fallbackPositions: moveMenuFallbackPositions,
        }),
        [moveMenuFallbackPositions]
    );

    const moveMenuPositioning = React.useMemo<PositioningProps>(() => {
        return shouldLockMoveMenuPosition
            ? {
                  ...moveMenuBasePositioning,
                  disableUpdateOnResize: true,
              }
            : {
                  ...moveMenuBasePositioning,
                  onPositioningEnd: handleMoveMenuPositioningEnd,
              };
    }, [handleMoveMenuPositioningEnd, moveMenuBasePositioning, shouldLockMoveMenuPosition]);

    const positioning: PositioningProps = React.useMemo(
        () => (target ? { target } : moveMenuPositioning),
        [target, moveMenuPositioning]
    );

    const dismissMoveMenu = React.useCallback(
        (shouldFocusTrigger: boolean = false) => {
            setMoveMenuOpen(false);
            onMenuCloseCallback?.(shouldFocusTrigger);
            setShouldLockMoveMenuPosition(false);
            menuProps?.onMenuDismissed?.();
            menuProps?.onDismiss?.();
        },
        [menuProps]
    );

    const handleMoveMenuKeyDown = React.useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isMoveMenuOpen) {
                // event.stopPropagation();
                dismissMoveMenu(true);
            }
        },
        [dismissMoveMenu, isMoveMenuOpen]
    );

    React.useEffect(() => {
        if (!isMoveMenuOpen) {
            return;
        }

        const removeListener = addDocumentEventListener(
            'IconBarMoveMenuEscape',
            'keydown',
            handleMoveMenuKeyDown,
            undefined,
            true
        );

        return () => removeListener?.();
    }, [handleMoveMenuKeyDown, isMoveMenuOpen]);

    const onMenuOpenChange: MenuProps['onOpenChange'] = React.useCallback(
        (_e: unknown, data: MenuOpenChangeData) => {
            setMoveMenuOpen(data.open);
            if (!data.open) {
                dismissMoveMenu();
            }
        },
        [dismissMoveMenu]
    );

    const menuItemClickHandlers = React.useMemo(
        () =>
            items.map(item => {
                const dismiss = menuProps?.onDismiss;
                return (ev: React.MouseEvent<HTMLElement>) => {
                    item.onClick?.(ev, item as IContextualMenuItem);
                    dismiss?.(ev);
                };
            }),
        [items, menuProps?.onDismiss]
    );

    const standardMenuItems = React.useMemo(() => {
        return items.map((item, index) => {
            if (item.itemType === 1 /* Divider */) {
                return <MenuDivider key={item.key || item.text} />;
            }

            const clickHandler = menuItemClickHandlers[index];
            return (
                <MenuItem
                    key={item.key || item.text}
                    disabled={!!item.disabled}
                    onClick={clickHandler}
                >
                    {item.text}
                </MenuItem>
            );
        });
    }, [items, menuItemClickHandlers]);

    const customMenuItems = React.useMemo(() => {
        if (!hasCustomRenderItems) {
            return null;
        }

        return items.map(item => {
            const key = item.key || item.text || 'custom';
            return (
                <React.Fragment key={key}>
                    {item.onRender?.(
                        item as IContextualMenuItem,
                        menuProps?.onDismiss ?? (() => undefined)
                    )}
                </React.Fragment>
            );
        });
    }, [items, hasCustomRenderItems, menuProps]);

    const menuPopover = (
        <MenuPopover>
            {hasCustomRenderItems ? (
                <div>{customMenuItems}</div>
            ) : (
                <MenuList>{standardMenuItems}</MenuList>
            )}
        </MenuPopover>
    );

    return (
        <Menu
            open={open ?? isMoveMenuOpen}
            onOpenChange={onMenuOpenChange}
            positioning={positioning}
        >
            {children
                ? ([<MenuTrigger key="trigger">{children}</MenuTrigger>, menuPopover] as const)
                : menuPopover}
        </Menu>
    );
});
